import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';

import TrendHistoryChart from './components/TrendHistoryChart';
import TrendEmptyState from './components/TrendEmptyState';
import { useTrendsSignals } from './hooks/useTrendsSignals';
import { fetchTrendSignalHistory } from './service';
import type { TrendSignalSnapshot } from './types';
import { DISPLAY_FONT, TREND_COLORS } from './trendTheme';

function trendTrackStatusLabel(status: string): string {
  if (status === 'new') return 'Nytt';
  if (status === 'rising') return 'Växer';
  if (status === 'stable') return 'Stabilt';
  if (status === 'declining') return 'Avtar';
  return status || 'Okänd status';
}

export default function TrendsHistoryPage() {
  const { signals, loading, error } = useTrendsSignals();
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<TrendSignalSnapshot[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!selectedSignalId && signals[0]?._id) {
      setSelectedSignalId(signals[0]._id);
    }
  }, [signals, selectedSignalId]);

  useEffect(() => {
    if (!selectedSignalId) {
      setSnapshots([]);
      return;
    }

    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        setSnapshots(await fetchTrendSignalHistory(selectedSignalId));
      } finally {
        setHistoryLoading(false);
      }
    };
    void loadHistory();
  }, [selectedSignalId]);

  if (loading) {
    return <CircularProgress sx={{ color: TREND_COLORS.navy }} />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.6rem', fontWeight: 700, color: TREND_COLORS.navy }}>
          Trendhistorik
        </Typography>
        <Typography sx={{ color: TREND_COLORS.textSecondary }}>
          Följ hur enskilda trendsignaler utvecklas över tid – styrka, status och rörelse vecka för vecka. Skiljer sig från körningshistoriken under Status & Körning, som visar själva pipelinekörningarna.
        </Typography>
      </Box>
      {signals.length === 0 ? (
        <TrendEmptyState
          title="Ingen trendhistorik ännu"
          description="När pipelinen har identifierat trendsignaler visas deras utveckling över tid här."
        />
      ) : (
      <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Paper sx={{ p: 1.5, height: '100%' }}>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.1rem', fontWeight: 700, color: TREND_COLORS.navy, px: 1, pb: 1 }}>
            Trendsignaler
          </Typography>
          <List dense>
            {signals.map((signal) => (
              <ListItemButton
                key={signal._id}
                selected={signal._id === selectedSignalId}
                onClick={() => setSelectedSignalId(signal._id)}
              >
                <ListItemText
                  primary={signal.label}
                  secondary={`${trendTrackStatusLabel(signal.status)} • rörelse ${signal.momentumScore}`}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Stack spacing={2}>
          {historyLoading ? (
            <CircularProgress sx={{ color: TREND_COLORS.navy }} />
          ) : (
            <TrendHistoryChart
              title={signals.find((signal) => signal._id === selectedSignalId)?.label || 'Trendtecken'}
              snapshots={snapshots}
            />
          )}
        </Stack>
      </Grid>
      </Grid>
      )}
    </Stack>
  );
}
